import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Calendar, DollarSign, TrendingUp, TrendingDown, Clock, Download, AlertTriangle, Calculator, BarChart3 } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const ContractAnalytics = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [users, setUsers] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUsers();
      fetchAnalytics();
    }
  }, [user, selectedPeriod, selectedYear, selectedUser, selectedStatus]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = {
        period: selectedPeriod,
        year: selectedYear
      };
      if (selectedUser !== 'all') {
        params.user_id = selectedUser;
      }
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }

      const response = await axios.get(`${API_BASE_URL}/contracts/analytics/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
        params
      });
      setAnalyticsData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: analyticsData?.currency || 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('Cost') || entry.name.includes('Rate')
                ? formatCurrency(entry.value)
                : entry.value.toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        year: selectedYear,
        ...(selectedUser !== 'all' && { user_id: selectedUser }),
        ...(selectedStatus !== 'all' && { status: selectedStatus })
      });

      const response = await fetch(`${API_BASE_URL}/contracts/analytics/export/?${params}`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contract_analytics_${selectedYear}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error exporting data');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting data');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {/* Skeleton Header */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-2"></div>
              <div className="h-3 w-32 bg-gray-100 rounded animate-pulse"></div>
            </div>
            <div className="flex gap-2">
              <div className="h-8 w-24 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-8 w-24 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
        {/* Skeleton Cards */}
        <div className="flex gap-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex-1 bg-white rounded-lg shadow p-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                <div>
                  <div className="h-3 w-12 bg-gray-100 rounded animate-pulse mb-1"></div>
                  <div className="h-5 w-16 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Skeleton Chart */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-4"></div>
          <div className="h-48 bg-gray-100 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <BarChart3 className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Analytics Data</h3>
        <p className="text-sm text-gray-500">
          There's no contract data available for the selected period.
          Try selecting a different year or adding contracts.
        </p>
      </div>
    );
  }

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="space-y-4">
      {/* Header & Filters */}
      <div className="bg-white rounded-lg shadow p-4 print:shadow-none">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Contract Analytics</h2>
            <p className="text-xs text-gray-500">Cost analysis and rate history</p>
          </div>

          <div className="flex flex-wrap gap-2 print:hidden">
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Users</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="terminated">Terminated</option>
            </select>

            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="month">Monthly</option>
              <option value="quarter">Quarterly</option>
              <option value="year">Yearly</option>
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            <button
              onClick={handleExport}
              disabled={exporting}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Download className="w-3.5 h-3.5" />
              {exporting ? 'Exporting...' : 'Excel'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="flex flex-wrap lg:flex-nowrap gap-2">
        <div className="flex-1 min-w-[120px] bg-white rounded-lg shadow p-2.5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-3.5 h-3.5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500 truncate">Total</p>
              <div className="flex items-center gap-1">
                <p className="text-base font-bold text-gray-900">
                  {analyticsData.summary?.total_contracts || 0}
                </p>
                {analyticsData.summary?.trend_total !== 0 && (
                  <span className={`text-[9px] flex items-center ${analyticsData.summary?.trend_total > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analyticsData.summary?.trend_total > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                    {Math.abs(analyticsData.summary?.trend_total || 0)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[120px] bg-white rounded-lg shadow p-2.5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-3.5 h-3.5 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500 truncate">Active</p>
              <div className="flex items-center gap-1">
                <p className="text-base font-bold text-green-600">
                  {analyticsData.summary?.active_contracts || 0}
                </p>
                {analyticsData.summary?.trend_active !== 0 && (
                  <span className={`text-[9px] flex items-center ${analyticsData.summary?.trend_active > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analyticsData.summary?.trend_active > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                    {Math.abs(analyticsData.summary?.trend_active || 0)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[120px] bg-white rounded-lg shadow p-2.5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-3.5 h-3.5 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500 truncate">Avg Rate</p>
              <div className="flex items-center gap-1">
                <p className="text-base font-bold text-gray-900">
                  {formatCurrency(analyticsData.summary?.average_rate || 0)}
                </p>
                {analyticsData.summary?.trend_rate !== 0 && (
                  <span className={`text-[9px] flex items-center ${analyticsData.summary?.trend_rate > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analyticsData.summary?.trend_rate > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                    {Math.abs(analyticsData.summary?.trend_rate || 0)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[120px] bg-white rounded-lg shadow p-2.5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Clock className="w-3.5 h-3.5 text-orange-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500 truncate">Hours</p>
              <div className="flex items-center gap-1">
                <p className="text-base font-bold text-gray-900">
                  {analyticsData.summary?.total_hours?.toLocaleString() || 0}
                </p>
                {analyticsData.summary?.trend_hours !== 0 && (
                  <span className={`text-[9px] flex items-center ${analyticsData.summary?.trend_hours > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analyticsData.summary?.trend_hours > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                    {Math.abs(analyticsData.summary?.trend_hours || 0)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[120px] bg-white rounded-lg shadow p-2.5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calculator className="w-3.5 h-3.5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500 truncate">Total Cost</p>
              <p className="text-base font-bold text-gray-900">
                {formatCurrency(analyticsData.summary?.total_cost || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[120px] bg-white rounded-lg shadow p-2.5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-3.5 h-3.5 text-cyan-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500 truncate">Cost/Hour</p>
              <p className="text-base font-bold text-gray-900">
                {formatCurrency(analyticsData.summary?.avg_cost_per_hour || 0)}
              </p>
            </div>
          </div>
        </div>

        {analyticsData.summary?.expiring_soon > 0 && (
          <div className="flex-1 min-w-[120px] bg-white rounded-lg shadow p-2.5 border-l-2 border-orange-500">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-gray-500 truncate">Expiring</p>
                <p className="text-base font-bold text-orange-600">
                  {analyticsData.summary?.expiring_soon || 0}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cost Analysis Chart */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Cost Analysis by {selectedPeriod === 'month' ? 'Month' : selectedPeriod === 'quarter' ? 'Quarter' : 'Year'}
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={analyticsData.cost_by_period || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="total_cost" fill="#3b82f6" name="Total Cost" />
            <Bar dataKey="registered_hours" fill="#10b981" name="Registered Hours" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Rate History & Rate vs Hours - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Rate History Chart */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Rate History</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={analyticsData.rate_history || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="rate"
                stroke="#8b5cf6"
                fill="#8b5cf6"
                fillOpacity={0.3}
                name="Rate"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Rate vs Hours Chart */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Rate vs Registered Hours</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={analyticsData.rate_vs_hours || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="rate"
                stroke="#3b82f6"
                name="Rate"
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="hours"
                stroke="#10b981"
                name="Hours"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cost per User & Rate Type Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cost/Rate per User - Horizontal Bar */}
        {analyticsData.cost_per_user && analyticsData.cost_per_user.length > 0 && (
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              {analyticsData.cost_per_user.some(u => u.cost > 0) ? 'Cost per User' : 'Rate per User'}
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analyticsData.cost_per_user} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey={analyticsData.cost_per_user.some(u => u.cost > 0) ? 'cost' : 'rate'}
                  fill="#3b82f6"
                  name={analyticsData.cost_per_user.some(u => u.cost > 0) ? 'Cost' : 'Rate'}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Rate Type Breakdown - Pie Chart */}
        {analyticsData.rate_type_breakdown && analyticsData.rate_type_breakdown.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Rate Type Breakdown</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={analyticsData.rate_type_breakdown}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ type, percentage }) => `${type} ${percentage}%`}
                  labelLine={false}
                >
                  {analyticsData.rate_type_breakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top Contracts */}
      {analyticsData.top_contracts && analyticsData.top_contracts.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Top 5 Contracts by Rate</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-medium text-gray-600">#</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-600">User</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-600">Contract</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-600">Rate</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-600">Hours</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-600">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.top_contracts.map((contract, index) => (
                  <tr key={index} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 px-2 text-gray-500">{index + 1}</td>
                    <td className="py-2 px-2 font-medium text-gray-900">{contract.user_name}</td>
                    <td className="py-2 px-2 text-gray-600">{contract.contract_number}</td>
                    <td className="py-2 px-2 text-right">
                      {formatCurrency(contract.rate)}
                      <span className="text-xs text-gray-400 ml-1">/{contract.rate_type}</span>
                    </td>
                    <td className="py-2 px-2 text-right text-gray-600">{contract.hours}</td>
                    <td className="py-2 px-2 text-right font-medium text-gray-900">
                      {formatCurrency(contract.total_cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alerts Panel */}
      {analyticsData.alerts && analyticsData.alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            Alerts ({analyticsData.alerts.length})
          </h3>
          <div className="space-y-2">
            {analyticsData.alerts.map((alert, index) => (
              <div key={index} className={`flex items-center justify-between p-2 rounded text-sm ${
                alert.severity === 'critical' ? 'bg-red-50' : 'bg-orange-50'
              }`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    alert.severity === 'critical' ? 'bg-red-500' : 'bg-orange-500'
                  }`}></span>
                  <span className="font-medium">{alert.contract_number}</span>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-600">{alert.user_name}</span>
                </div>
                <span className={`text-xs font-medium ${
                  alert.severity === 'critical' ? 'text-red-600' : 'text-orange-600'
                }`}>
                  {alert.message}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Currency Breakdown */}
      {analyticsData.currency_breakdown && analyticsData.currency_breakdown.length > 1 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Currency Breakdown</h3>
          <div className="flex flex-wrap gap-3">
            {analyticsData.currency_breakdown.map((curr, index) => {
              const symbols = { EUR: '€', PLN: 'zł', USD: '$', GBP: '£' };
              const symbol = symbols[curr.currency] || curr.currency;
              return (
                <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <span className="font-bold text-gray-900">{curr.currency}</span>
                  <span className="text-sm text-gray-500">
                    {curr.count} contract{curr.count !== 1 ? 's' : ''} • {symbol}{curr.total_rate.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Amendments Timeline */}
      {analyticsData.recent_amendments && analyticsData.recent_amendments.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Rate Amendments</h3>
          <div className="space-y-2">
            {analyticsData.recent_amendments.slice(0, 5).map((amendment, index) => {
              const change = amendment.new_rate - amendment.previous_rate;
              const percentChange = ((change / amendment.previous_rate) * 100).toFixed(1);

              return (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-gray-900 truncate">{amendment.user_name}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{new Date(amendment.effective_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <span className="text-gray-600">
                      {formatCurrency(amendment.previous_rate)} → {formatCurrency(amendment.new_rate)}
                    </span>
                    <span className={`text-xs font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {change >= 0 ? '+' : ''}{percentChange}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractAnalytics;
